import { queryOptions, skipToken, useQuery } from '@tanstack/react-query';
import { z } from 'zod';
import { groupMemberScheme } from '@/models/group/group-info';
import { generateListResponseScheme, type ListRespondedWith } from '@/models/response';
import { fetcher } from '@/utils/fetcher';

const searchGroupMembersSchema = groupMemberScheme
  .pick({
    GroupID: true,
    GroupImg: true,
    GroupName: true,
  })
  .extend({
    Users: z.array(
      z.object({
        UserID: z.number(),
        MatchedName: z.string(),
      }),
    ),
  });

export type SearchGroupMember = z.infer<typeof searchGroupMembersSchema>;

const generateSearchGroupMembersQueryOptions = (keyword: Nullable<string>) =>
  queryOptions({
    queryKey: ['/SearchMyGroupAllUsers', keyword],
    queryFn: keyword
      ? ({ signal }) =>
          fetcher<ListRespondedWith<SearchGroupMember>>(
            '/SearchMyGroupAllUsers',
            { Keyword: keyword },
            {
              signal,
              schema: generateListResponseScheme(searchGroupMembersSchema),
              validResponseCodes: [0, 1],
            },
          ).then((response) => response?.Data ?? [])
      : skipToken,
    staleTime: 15_000,
    gcTime: 0,
  });

export default function useSearchGroupMembers(keyword = '') {
  const query = useQuery(generateSearchGroupMembersQueryOptions(keyword));

  return {
    groupMembers: query.data ?? [],
    ...query,
  };
}
